import React, { useEffect, useState } from "react";
import {
  Modal,
  Button,
  Spinner,
  ProgressBar,
  Form,
  Table,
} from "react-bootstrap";
import {
  getDriveSelectedDate,
  uploadToServer,
  uploadToDrive,
  checkFile,
  deleteFile,
  getUserFiles,
} from "../../api/drive-service";
import { toast } from "../../helpers/swal";
import { getDateDayMountYear } from "../../helpers/functions/date-time";

const EMB_PARENT_FOLDER_ID = "1ArBT61fWRFwh4iqpgzTl-hHdQ8WnSbGn";

const EmbroideryUploadModal = ({ show, onClose, order }) => {
  const [folders, setFolders] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [filteredFolders, setFilteredFolders] = useState([]);
  const [selectedFolderId, setSelectedFolderId] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [userFiles, setUserFiles] = useState([]);

  useEffect(() => {
    if (show) {
      fetchFolders();
      fetchUserFiles();
      resetStates();
    }
  }, [show]);

  const resetStates = () => {
    setSelectedDate("");
    setFilteredFolders([]);
    setSelectedFolderId("");
    setSelectedFile(null);
    setUploading(false);
    setProgress(0);
    setUploadSuccess(false);
  };

  const fetchFolders = async () => {
    try {
      const res = await getDriveSelectedDate(EMB_PARENT_FOLDER_ID);
      setFolders(res.data || []);
    } catch (err) {
      toast("Failed to fetch folders", "error");
    }
  };

  const fetchUserFiles = async () => {
    try {
      const user_id = localStorage.getItem("user_id");
      const res = await getUserFiles(user_id);

      setUserFiles(res.data || []);
    } catch (err) {
      toast("Failed to fetch uploaded files", "error");
    }
  };

  const handleDateChange = async (e) => {
    const dateValue = e.target.value;
    setSelectedDate(dateValue);

    const formattedDate = getDateDayMountYear(dateValue);

    const matchingFolder = folders.find(
      (folder) => folder.name === formattedDate
    );

    if (matchingFolder) {
      try {
        const resp = await getDriveSelectedDate(matchingFolder.id);
        setFilteredFolders(resp.data || []);
      } catch (err) {
        toast("Failed to fetch subfolders", "error");
        setFilteredFolders([]);
      }
    } else {
      setFilteredFolders([]);
      toast("No matching folder found for this date", "warning");
    }
  };

const handleUpload = async () => {
  if (!selectedFile || !selectedFolderId) {
    toast("Please select both file and folder", "warning");
    return;
  }

  setUploading(true);
  setProgress(0);

  let currentProgress = 0;

  const simulateProgress = () => {
    const increment = Math.floor(Math.random() * 10) + 5;
    currentProgress = Math.min(currentProgress + increment, 95);
    setProgress(currentProgress);
    if (currentProgress < 95) {
      setTimeout(simulateProgress, 400);
    }
  };

  simulateProgress();

  try {
    const user_id = localStorage.getItem("user_id");
    const formattedDate = getDateDayMountYear(selectedDate);

    // ✅ KLASÖR ADINI ALIYORUZ (Doğru gösterim için)
    const selectedFolderName = filteredFolders.find(folder => folder.id === selectedFolderId)?.name || "EMBROIDERY";

    // ✅ UPLOAD TO SERVER → Klasör adını gönderiyoruz
    const resp = await uploadToServer(user_id, selectedFolderName, selectedFile, formattedDate);
    const temp_path = resp.data.response.temp_path;
    const temp_id = resp.data.response.temp_id;

    // ✅ UPLOAD TO DRIVE
    const driveResp = await uploadToDrive(temp_id, temp_path, selectedFolderId, selectedFolderName);

    // ✅ CHECK FILE → Status = DONE
    await checkFile(temp_id, driveResp.data.file_id, temp_path);

    setProgress(100);
    setUploadSuccess(true);
    toast("Uploaded successfully", "success");
    fetchUserFiles();

  } catch (err) {
    toast("Upload failed", "error");
  } finally {
    setUploading(false);
  }
};


  const handleDeleteFile = (fileId) => {
    deleteFile(fileId)
      .then(() => {
        toast("File deleted successfully", "success");
        fetchUserFiles();
      })
      .catch(() => {
        toast("Failed to delete file", "error");
      });
  };

  const formatFileSize = (fileSize) => {
    if (!fileSize) return "N/A";
    if (typeof fileSize === "string") return fileSize; // Direkt göster
    const mb = fileSize / 1024 / 1024;
    return mb.toFixed(2) + " MB";
  };

  const formatDimensions = (w, h) => {
    if (!w || !h) return "N/A";
    return `${w.toFixed(1)} x ${h.toFixed(1)}`;
  };

  const getStatusText = (file) => {
    if (file.deleted == 1) return "DELETED";
    if (file.success == 1) return "DONE";
    return "ERROR";
  };

  const getStatusColor = (file) => {
    if (file.deleted == 1) return "orange";
    if (file.success == 1) return "lightgreen";
    return "red";
  };

  return (
  <Modal
  show={show}
  onHide={onClose}
  size="xl"
  centered
  dialogClassName="custom-modal-width"
>



      <Modal.Header closeButton>
        <Modal.Title>
          Embroidery Upload for Order #{order?.orderNumber}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>Select Date</Form.Label>
          <Form.Control
            type="date"
            value={selectedDate}
            onChange={handleDateChange}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Select Folder</Form.Label>
          <Form.Select
            value={selectedFolderId}
            onChange={(e) => setSelectedFolderId(e.target.value)}
            disabled={filteredFolders.length === 0}
          >
            <option value="">-- Select Folder --</option>
            {filteredFolders.map((folder) => (
              <option key={folder.id} value={folder.id}>
                {folder.name}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Select PNG File</Form.Label>
          <Form.Control
            type="file"
            accept=".png"
            onChange={(e) => setSelectedFile(e.target.files[0])}
          />
        </Form.Group>

        {uploading && (
          <ProgressBar
            now={progress}
            label={`${progress}%`}
            striped
            animated
            className="mb-3"
          />
        )}

        {uploadSuccess && (
          <div
            style={{ color: "green", fontWeight: "bold", marginBottom: "10px" }}
          >
            ✅ Uploaded Successfully
          </div>
        )}

        <Table
          striped
          bordered
          hover
          responsive
          style={{ textAlign: "center", marginTop: "20px" }}
        >
          <thead>
            <tr>
              <th>File Name</th>
              <th>Folder Name</th>
              <th>Size</th>
              <th>Dimensions (in)</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {userFiles.map((file, index) => (
              <tr key={index}>
                <td>{file.file_name}</td>
                <td>{file.folderName}</td>
                <td>{formatFileSize(file.file_size)}</td>
                <td>{formatDimensions(file.inch_width, file.inch_height)}</td>
                <td
                  style={{
                    backgroundColor: getStatusColor(file),
                    color: "white",
                  }}
                >
                  {getStatusText(file)}
                </td>
                <td>
                  {file.deleted != 1 && (
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => handleDeleteFile(file.file_id)}
                    >
                      Delete
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={uploading}>
          Close
        </Button>
        <Button variant="primary" onClick={handleUpload} disabled={uploading}>
          {uploading ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
              />{" "}
              Uploading...
            </>
          ) : (
            "Upload"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EmbroideryUploadModal;
